// src/components/SEO/SEOHelmet.jsx
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { getTranslation, normalizeLang } from '../../helpers/translation';

const BASE_URL = 'https://artemidastudio.com';
const SUPPORTED = ['en', 'de', 'it', 'fr', 'es', 'pt', 'nl', 'da', 'pl', 'ua'];

// BCP-47: 'ua' -> 'uk'
const toHrefLang = code => (code === 'ua' ? 'uk' : code);

// локалізовані префікси (не ріжемо en)
const LOCALE_RE = /^\/(de|it|fr|es|pt|nl|da|pl|ua)(?=\/|$)/;

const clean = p => (p || '/').replace(/\/+$/, '') || '/';

// Домашня лишається '/', для постів нічого не чіпаємо
function maybeKeepSlash(path) {
  if (path === '/') return '/';
  if (/\/blog\/post\//.test(path)) return path;
  if (/\.[a-z0-9]+$/i.test(path)) return path;
  return path.endsWith('/') ? path : path;
}

function stripLocale(pathname) {
  const stripped = (pathname || '/').replace(LOCALE_RE, '') || '/';
  return clean(stripped);
}

function buildCanonical(base, lang, location, overrideCanonical) {
  if (overrideCanonical) {
    const path = maybeKeepSlash(clean(overrideCanonical));
    const search = location.search || '';
    return `${base}${path}${search}`;
  }
  const pathNoLocale = stripLocale(location.pathname);
  const search = location.search || '';
  let path = pathNoLocale;
  if (lang !== 'en') path = path === '/' ? `/${lang}/` : `/${lang}${path}`;
  path = maybeKeepSlash(path);
  return `${base}${path}${search}`;
}

function buildHrefLangs(base, location) {
  const pathNoLocale = stripLocale(location.pathname);

  const entries = SUPPORTED.map(code => {
    let hrefPath = pathNoLocale;
    if (code !== 'en') {
      hrefPath = hrefPath === '/' ? `/${code}/` : `/${code}${hrefPath}`;
    }
    hrefPath = maybeKeepSlash(clean(hrefPath));
    return { code: toHrefLang(code), href: `${base}${hrefPath}` };
  });

  const enHrefPath = maybeKeepSlash(clean(pathNoLocale));
  const xDefault = { code: 'x-default', href: `${base}${enHrefPath}` };

  return { alternates: entries, xDefault };
}

// Допоміжне: абсолютний URL для зображень/посилань
function absoluteUrl(u) {
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
}

// Будуємо breadcrumbs: Home -> Blog -> (optional) Post Title
function buildBreadcrumbs({ lang, location, t, canonical, postTitle }) {
  const items = [];
  const homeName = t?.header?.homeLink || 'Home';
  const blogName = t?.header?.blogLink || 'Blog';

  // Home
  const homeHref = lang === 'en' ? `${BASE_URL}/` : `${BASE_URL}/${lang}/`;
  items.push({ name: homeName, item: homeHref });

  const pathNoLocale = stripLocale(location.pathname);
  const parts = pathNoLocale.split('/').filter(Boolean);

  // /blog
  if (parts[0] === 'blog') {
    const blogHref =
      lang === 'en' ? `${BASE_URL}/blog` : `${BASE_URL}/${lang}/blog`;
    items.push({ name: blogName, item: blogHref });

    // /blog/post/:slug/:id
    if (parts[1] === 'post' && postTitle && canonical) {
      items.push({ name: postTitle, item: canonical });
    }
  }

  // Можеш розширити логіку для інших секцій (services/about), якщо треба
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };
}

/**
 * Пропси:
 * - pageKey: ключ у перекладах (t.seo[pageKey])
 * - override:
 *   {
 *     title, description, keywords, canonical, ogImage,
 *     article?: {
 *       datePublished, dateModified, authorName
 *     }
 *   }
 */
export default function SEOHelmet({ pageKey = 'home', override }) {
  const { lang: rawLang } = useParams();
  const location = useLocation();
  const lang = normalizeLang(rawLang); // 'en' якщо undefined
  const langCode = toHrefLang(lang);

  const t = useMemo(() => getTranslation(lang), [lang]);
  const fromI18n = t?.seo?.[pageKey] || {};

  const meta = {
    title: override?.title || fromI18n.title || 'Artemida Studio',
    description:
      override?.description ||
      fromI18n.description ||
      'We create custom websites of any complexity',
    keywords:
      override?.keywords ||
      fromI18n.keywords ||
      'web, website, landing, corporate, AI',
    ogImage: absoluteUrl(
      override?.ogImage ||
        'https://dating-date-photos.s3.us-east-2.amazonaws.com/Logo+IKA.png',
    ),
  };

  const canonical = buildCanonical(
    BASE_URL,
    lang,
    location,
    override?.canonical,
  );
  const { alternates, xDefault } = buildHrefLangs(BASE_URL, location);

  const isArticle = /\/blog\/post\//.test(location.pathname);

  // -------- JSON-LD блоки --------
  const orgId = `${BASE_URL}/#org`;
  const siteId = `${BASE_URL}/#website`;

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': orgId,
    name: 'Artemida Studio',
    url: BASE_URL,
    logo: { '@type': 'ImageObject', url: meta.ogImage },
    // sameAs: ['https://www.instagram.com/...','https://www.linkedin.com/company/...']
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': siteId,
    url: BASE_URL,
    name: 'Artemida Studio',
    inLanguage: langCode,
    publisher: { '@id': orgId },
    // potentialAction: {
    //   '@type': 'SearchAction',
    //   target: `${BASE_URL}/search?q={search_term_string}`,
    //   'query-input': 'required name=search_term_string'
    // }
  };

  const breadcrumb = buildBreadcrumbs({
    lang,
    location,
    t,
    canonical,
    postTitle: meta.title,
  });

  const article = isArticle
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        headline: meta.title,
        description: meta.description,
        image: meta.ogImage ? [meta.ogImage] : undefined,
        inLanguage: langCode,
        datePublished: override?.article?.datePublished,
        dateModified:
          override?.article?.dateModified || override?.article?.datePublished,
        author: override?.article?.authorName
          ? { '@type': 'Person', name: override.article.authorName }
          : { '@type': 'Organization', name: 'Artemida Studio', '@id': orgId },
        publisher: {
          '@type': 'Organization',
          name: 'Artemida Studio',
          '@id': orgId,
          logo: { '@type': 'ImageObject', url: meta.ogImage },
        },
        // articleSection: 'Blog',
        // keywords: meta.keywords?.split(',').map(s => s.trim()).filter(Boolean),
      }
    : null;

  const jsonLd = [
    organization,
    website,
    breadcrumb,
    ...(article ? [article] : []),
  ];

  return (
    <Helmet htmlAttributes={{ lang: langCode }}>
      <title>{meta.title}</title>

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* hreflang */}
      {alternates.map(({ code, href }) => (
        <link key={code} rel="alternate" hrefLang={code} href={href} />
      ))}
      <link rel="alternate" hrefLang={xDefault.code} href={xDefault.href} />

      {/* Primary Meta */}
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />

      {/* Open Graph */}
      <meta property="og:type" content={isArticle ? 'article' : 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      {meta.ogImage && <meta property="og:image" content={meta.ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {meta.ogImage && <meta name="twitter:image" content={meta.ogImage} />}

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
